import { midwivesCollection } from '../../collections/collections/practice'
import { FieldTypes } from '../../constants'
import { AppName, DeliveryLocation, PracticeWithAdmin } from '../../types'
import {
  BooleanField,
  CheckboxField,
  DateField,
  DropdownField,
  FieldMap,
  IdField,
  NumberField,
  TextAreaField,
} from '../../types/forms/fields'
import { getMidwifeFullName } from '../../utils/providers'
import { hasPartnerField, partnerContactField } from '../partner'

export const midwifeId: IdField<PracticeWithAdmin> = {
  collection: midwivesCollection,
  type: FieldTypes.ID,
  getItemText: item => getMidwifeFullName(item),
  searchable: true,
  placeholder: 'Midwife',
  labels: [{ text: 'Which midwife would you like to work with?' }],
}

export const patientLmpField: DateField = {
  type: FieldTypes.DATE,
  labels: [{ text: 'What was the first day of your last menstrual cycle?' }],
  placeholder: 'First day of last menstrual cycle',
  optional: true,
}

export const eddField: DateField = {
  type: FieldTypes.DATE,
  // validate: validateEdd,
  minDate: 'now',
  placeholder: 'Expected Date of Delivery',
}

export const smallEddField: DateField = {
  ...eddField,
  placeholder: 'EDD',
}

export const hasPriorCSectionField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  labels: [{ text: 'Have you had a Cesarean section in the past?' }],
  placeholder: 'Previous Cesarean section?',
}

export const basePriorCSectionDateField: DateField = {
  type: FieldTypes.DATE,
  labels: [{ text: 'What was the date of your most recent C-Section?' }],
  placeholder: 'Date of most recent C-Section',
}
export const priorCSectionDateField: DateField = {
  ...basePriorCSectionDateField,
  condition: val => !!val?.hasPriorCSection,
}

export const seenAnotherProviderField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  labels: [
    {
      text: 'Have you seen another provider for this pregnancy for any prenatal care?',
    },
  ],
  placeholder: "I've seen another provider",
}

export const plannedDeliveryLocationField: DropdownField<DeliveryLocation> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Delivery Location',
  labels: [{ text: 'What type of delivery are you interested in?' }],
  options: [
    { id: 'home-birth', text: 'Home birth' },
    { id: 'facility-birth', text: 'Facility birth' },
    {
      id: 'birthing-center-birth',
      text: 'Birthing center birth',
      redFlagReason: 'Birthing center birth',
    },
    { id: 'undecided', text: 'Undecided', redFlagReason: 'Undecided on birth location' },
  ],
}

export const prenatalVisitsCountField: NumberField = {
  type: FieldTypes.NUMBER,
  labels: [{ text: 'How many prenatal visits have you had?' }],
  placeholder: 'Number of visits',
  condition: val => !!val?.seenAnotherProvider,
  min: 0,
}

export const prenatalVisitsDescriptionField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  labels: [
    {
      text: 'If you are not sure if you have received any prenatal care, please describe the nature of the visits',
    },
  ],
  placeholder: 'Describe your previous prenatal visits',
  condition: val => !!val?.seenAnotherProvider,
  optional: true,
}

export const interestedInOtherProvidersField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  inputType: 'switch',
  placeholder: 'Are you interested in any other providers?',
}

const otherProvidersField: IdField<PracticeWithAdmin> = {
  type: FieldTypes.ID,
  allowMultiple: true,
  collection: midwivesCollection,
  getItemText: item => getMidwifeFullName(item),
  searchable: true,
  placeholder: 'Other providers',
  condition: val => !!val?.interestedInOtherProviders,
}

export const deliveryStage: FieldMap = {
  name: 'Pregnancy',
  initExpanded: true,
  children: {
    edd: {
      ...eddField,
      labels: [{ text: 'What is your expected delivery date?' }],
    },
    seenAnotherProvider: seenAnotherProviderField,
    prenatalVisitsCount: prenatalVisitsCountField,
    prenatalVisitsDescription: prenatalVisitsDescriptionField,
    deliveryLocation: plannedDeliveryLocationField,
    midwifeId,
    interestedInOtherProviders: interestedInOtherProvidersField,
    otherProviders: otherProvidersField,
    lmp: patientLmpField,
    hasPriorCSection: hasPriorCSectionField,
    priorCSectionDate: priorCSectionDateField,
    hasPartner: hasPartnerField,
    partnerContact: partnerContactField,
  },
}

export const adminDeliveryStage: FieldMap = {
  name: 'Pregnancy',
  initExpanded: true,
  children: {
    edd: {
      ...eddField,
      minDate: undefined,
      labels: [{ text: 'What is your expected delivery date?' }],
    },
    seenAnotherProvider: seenAnotherProviderField,
    prenatalVisitsCount: prenatalVisitsCountField,
    prenatalVisitsDescription: prenatalVisitsDescriptionField,
    deliveryLocation: plannedDeliveryLocationField,
    midwifeId,
    interestedInOtherProviders: interestedInOtherProvidersField,
    otherProviders: otherProvidersField,
    lmp: patientLmpField,
    hasPriorCSection: hasPriorCSectionField,
    priorCSectionDate: priorCSectionDateField,
    hasPartner: hasPartnerField,
    partnerContact: partnerContactField,
  },
}

export const midwifeOnlyDeliveryStage: FieldMap = {
  name: 'Provider',
  initExpanded: true,
  children: {
    midwifeId,
  },
}

export const getQuestionnaireDeliveryStage = (appName: AppName): FieldMap => {
  if (appName === 'providers-app') {
    return {
      ...deliveryStage,
      children: {
        edd: eddField,
        seenAnotherProvider: seenAnotherProviderField,
        prenatalVisitsCount: prenatalVisitsCountField,
        prenatalVisitsDescription: prenatalVisitsDescriptionField,
        deliveryLocation: plannedDeliveryLocationField,
        midwifeId,
        lmp: patientLmpField,
        hasPriorCSection: hasPriorCSectionField,
        priorCSectionDate: priorCSectionDateField,
        hasPartner: hasPartnerField,
        partnerContact: partnerContactField,
      },
    }
  }
  return adminDeliveryStage
}
